import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError, api } from '../api/client.ts';
import type { SignupStatus } from '../api/types.ts';
import { BrandPin, Icon } from '../components/Icon.tsx';
import { Field } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { useDocumentTitle } from '../lib/documentTitle.ts';
import { countOnce } from '../lib/funnel.ts';
import { useStore } from '../state/store.tsx';

const PASSWORD_MIN = 12;
const PASSWORD_MAX = 128;
const SUPPORT = 'mailto:support@mapmymoney.app?subject=Setting%20up%20my%20account';
/** Polls back off from 1 s to 5 s; about 45 s in all before saying it is slow. */
const POLL_DELAYS_MS = [1000, 1000, 1500, 1500, 2000, 2000, 3000, 3000, 4000, 5000, 5000, 5000, 5000, 5000];

type Phase = { kind: 'checking' } | { kind: 'slow' } | { kind: 'error' } | SignupStatus;

/** Back from a sign-up checkout: waits for the payment, then the new account sets its password here. */
export function WelcomeScreen() {
  useDocumentTitle(t('docTitle.welcome'));
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') ?? '';
  const [phase, setPhase] = useState<Phase>({ kind: 'checking' });
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setPhase({ state: 'expired' });
      return;
    }
    let live = true;
    let timer = 0;
    let attempt = 0;
    const check = async () => {
      let status: SignupStatus;
      try {
        status = await api.signupStatus(sessionId);
      } catch {
        if (live) setPhase({ kind: 'error' });
        return;
      }
      if (!live) return;
      if (status.state !== 'pending') return setPhase(status);
      const delay = POLL_DELAYS_MS[attempt++];
      if (delay === undefined) setPhase({ kind: 'slow' });
      else timer = window.setTimeout(() => void check(), delay);
    };
    setPhase({ kind: 'checking' });
    void check();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [sessionId, round]);

  const retry = () => setRound((r) => r + 1);
  return (
    <div className="signin welcome">
      <main className="signin__panel" id="main">
        <div className="signin__brand"><BrandPin size={40} /></div>
        {'state' in phase ? (
          phase.state === 'ready' ? <ClaimPassword sessionId={sessionId} email={phase.email ?? ''} />
          : phase.state === 'existing_account' ? <ExistingAccount email={phase.email ?? ''} />
          : phase.state === 'expired' ? <Expired />
          : null
        ) : phase.kind === 'checking' ? (
          <div className="welcome__status" role="status" aria-busy="true">
            <span className="billing-return__spinner welcome__spinner" aria-hidden="true" />
            <h1 className="signin__title">{t('welcome.confirming.title')}</h1>
            <p className="signin__subtitle">{t('welcome.confirming.body')}</p>
          </div>
        ) : (
          <div className="welcome__status" role="status">
            <h1 className="signin__title">{phase.kind === 'slow' ? t('welcome.slow.title') : t('error.title')}</h1>
            <p className="signin__subtitle">{phase.kind === 'slow' ? t('welcome.slow.body') : t('welcome.error')}</p>
            <button type="button" className="button button--primary button--lg" onClick={retry}>{t('welcome.slow.retry')}</button>
          </div>
        )}
      </main>
    </div>
  );
}

function ClaimPassword({ sessionId, email }: { sessionId: string; email: string }) {
  const { loadSession, savePlan, checkIn } = useStore();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const passwordId = useId();
  const titleId = useId();

  const reject = (message: string) => {
    setFieldError(message);
    passwordRef.current?.focus();
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFieldError(null);
    setRefused(null);
    if (password.length < PASSWORD_MIN) return reject(t('signin.passwordTooShort'));
    if (password.length > PASSWORD_MAX) return reject(t('signin.passwordTooLong'));
    setBusy(true);
    try {
      await api.claimPassword(sessionId, password);
      countOnce('paid');
      await loadSession();
      const { keepDraftAfterPayment } = await import('../start/afterPay.ts');
      const kept = await keepDraftAfterPayment({ savePlan, checkIn });
      navigate(kept === 'saved' ? '/?start=saved' : kept === 'skipped' ? '/plan?add=income' : '/start?welcome=1', { replace: true });
    } catch (error) {
      setBusy(false);
      const issue = error instanceof ApiError ? error.issueFor('password') : null;
      if (issue) reject(issue);
      else if (error instanceof ApiError && error.status === 409) setRefused(error.message);
      else if (error instanceof ApiError && error.status === 429) setRefused(t('signin.rateLimited'));
      else setRefused(t('signin.failed'));
    }
  };

  return (
    <>
      <p className="eyebrow welcome__eyebrow"><Icon name="checkCircle" size={14} />{t('welcome.ready.eyebrow')}</p>
      <h1 className="signin__title" id={titleId}>{t('welcome.ready.title')}</h1>
      <p className="signin__subtitle">{t('welcome.ready.body', { email })}</p>
      <form className="signin__form" aria-labelledby={titleId} onSubmit={(e) => void submit(e)} noValidate>
        <input type="email" autoComplete="username" value={email} readOnly hidden />
        <Field label={t('signin.password')} htmlFor={passwordId} error={fieldError} hint={t('signin.passwordHint')}>
          <div className="password-input">
            <input
              ref={passwordRef}
              id={passwordId}
              className="input"
              type={shown ? 'text' : 'password'}
              autoComplete="new-password"
              autoCapitalize="none"
              spellCheck={false}
              aria-invalid={fieldError ? true : undefined}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="password-input__toggle"
              aria-label={shown ? t('signin.passwordHide') : t('signin.passwordShow')}
              onClick={() => setShown((s) => !s)}
            >
              {shown ? t('signin.passwordHideShort') : t('signin.passwordShowShort')}
            </button>
          </div>
        </Field>
        <p className="signin__note">{t('welcome.ready.note', { email })}</p>
        <button type="submit" className="button button--primary button--block button--lg" disabled={busy}>
          {busy ? t('welcome.ready.pending') : t('welcome.ready.submit')}
        </button>
      </form>
      {refused && (
        <div className="signin__error" role="alert">
          <p>{refused}</p>
          <Link to="/signin" className="text-link">{t('welcome.expired.cta')}</Link>
        </div>
      )}
    </>
  );
}

function ExistingAccount({ email }: { email: string }) {
  return (
    <div className="welcome__status">
      <h1 className="signin__title">{t('welcome.existing.title')}</h1>
      <p className="signin__subtitle">{t('welcome.existing.body', { email })}</p>
      <Link to="/signin" className="button button--primary button--block button--lg">{t('welcome.existing.cta')}</Link>
    </div>
  );
}

function Expired() {
  return (
    <div className="welcome__status">
      <h1 className="signin__title">{t('welcome.expired.title')}</h1>
      <p className="signin__subtitle">{t('welcome.expired.body')}</p>
      <div className="welcome__actions">
        <Link to="/signin" className="button button--primary button--block button--lg">{t('welcome.expired.cta')}</Link>
        <a href={SUPPORT} className="button button--outline button--block">{t('welcome.expired.support')}</a>
      </div>
    </div>
  );
}
